import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ref, get, onValue, push, set, update, remove, runTransaction, onDisconnect,
} from 'firebase/database';
import { database } from '../firebase';
import { toast } from '../utils/toast';
import {
  deriveSession, getStages, getSnapshots, computeRanking, normalizeDx, voteVal, getSettings,
  getMyGaps, STEP_ORDER, stepIndex, GAP_LABELS,
  HISTORY_ROW_ID, PHYSICAL_ROW_ID, LABS_ROW_ID,
} from '../utils/deriveSession';
import { fuzzyMatch } from '../utils/matching';
import { makeResolver, clusterEntries, sameEntry } from '../utils/mergeMatch';
import { resolveCase, streamInfo } from '../utils/caseStreams';
import { computeClassGaps, textsFromSharedItems, presentStudentKeys } from '../utils/classGaps';

// Firebase keys cannot contain . # $ / [ ]
export const sanitizeKey = (s) => (s || '').trim().replace(/[.#$/[\]]/g, '_');

// Generates a globally-unique push key without writing anything.
const genKey = () => push(ref(database, '_')).key;

// Session children everyone may read. `individual` is intentionally NOT here:
// it is private (own entries + facilitator) and has its own listener below.
const PUBLIC_CHILDREN = [
  'meta', 'settings', 'rows', 'diffRows', 'items', 'votes',
  'probabilities', 'presence', 'case', 'snapshots', 'caseFlow',
];

const seedRows = () => {
  const now = Date.now();
  const rows = {};
  for (let i = 0; i < 3; i++) rows[genKey()] = { isCritical: false, createdAt: now + i };
  rows[genKey()] = { isCritical: true, createdAt: now + 3 };
  return rows;
};

const seedDiffRows = () => {
  const now = Date.now();
  const diffRows = {};
  for (let i = 0; i < 3; i++) diffRows[genKey()] = { createdAt: now + i };
  return diffRows;
};

/**
 * All Firebase session logic. Every mutation writes only its own path
 * (item, row, or the caller's personal vote/probability leaf), so
 * concurrent users can never overwrite each other. Totals are derived
 * client-side (see utils/deriveSession.js).
 *
 * `identityKey` (auth uid, or a persistent per-device key), not the typed
 * display name, is what owns votes, items, and facilitator rights.
 */
export default function useSession(sessionId, userName, identityKey, active, { spectator = false } = {}) {
  const [data, setData] = useState({});
  const [individualData, setIndividualData] = useState(null);
  const [loading, setLoading] = useState(true);
  const base = `sessions/${sessionId}`;

  // ---- Subscriptions (one listener per public child; RTDB multiplexes them) ----
  useEffect(() => {
    if (!active || !sessionId) return undefined;
    const unsubs = PUBLIC_CHILDREN.map((child) =>
      onValue(
        ref(database, `${base}/${child}`),
        (snap) => {
          setData((prev) => ({ ...prev, [child]: snap.val() }));
          if (child === 'meta') setLoading(false);
        },
        (error) => {
          console.error(`Listener error (${child}):`, error);
          if (child === 'meta') toast('Cannot read this session, check your connection or permissions.');
        }
      )
    );
    return () => {
      unsubs.forEach((u) => u());
      setData({});
      setLoading(true);
    };
  }, [active, sessionId, base]);

  // Read straight from meta so the private-entry listener below (and therefore
  // deriveSession's input) doesn't depend on deriveSession's own output.
  const isFacilitator = !!data?.meta?.facilitatorKey && data.meta.facilitatorKey === identityKey;

  // My own private subtree. The facilitator's listener covers every student, so
  // narrow it to their own node, which is why their screen stays empty.
  const myPrivate = useMemo(
    () => (isFacilitator ? individualData?.[identityKey] : individualData) || null,
    [individualData, isFacilitator, identityKey]
  );

  const derived = useMemo(
    () => deriveSession(data, identityKey, myPrivate),
    [data, identityKey, myPrivate]
  );

  // Latest state for callbacks without re-creating them on every snapshot.
  const latest = useRef({ data, derived, individualData });
  latest.current = { data, derived, individualData };

  // ---- Private individual-phase entries ----
  // Students subscribe only to their own node; the facilitator to all of them.
  useEffect(() => {
    if (!active || !sessionId || spectator || !identityKey) return undefined;
    const path = isFacilitator ? `${base}/individual` : `${base}/individual/${identityKey}`;
    const unsubscribe = onValue(
      ref(database, path),
      (snap) => setIndividualData(snap.val()),
      () => setIndividualData(null)
    );
    return () => {
      unsubscribe();
      setIndividualData(null);
    };
  }, [active, sessionId, base, spectator, identityKey, isFacilitator]);

  // ---- One-time seed (first joiner becomes facilitator) ----
  useEffect(() => {
    if (!active || !sessionId || spectator) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const metaSnap = await get(ref(database, `${base}/meta`));
        if (cancelled || metaSnap.exists()) return;
        const now = Date.now();
        // applyLocally: false, never render optimistic seeds; if two clients
        // race, the loser's local keys would roll back and orphan anything
        // typed into them. Server-confirmed data only.
        const opts = { applyLocally: false };
        await runTransaction(
          ref(database, `${base}/meta`),
          (cur) => (cur === null
            ? { createdAt: now, lastUpdated: now, facilitatorKey: identityKey, facilitatorName: userName }
            : undefined),
          opts
        );
        await runTransaction(ref(database, `${base}/rows`), (cur) => (cur === null ? seedRows() : undefined), opts);
        await runTransaction(ref(database, `${base}/diffRows`), (cur) => (cur === null ? seedDiffRows() : undefined), opts);
      } catch (error) {
        console.error('Session seed failed:', error);
        toast('Could not initialize the session.');
      }
    })();
    return () => { cancelled = true; };
    // identityKey/userName are fixed for the lifetime of a join.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sessionId, spectator]);

  // ---- Presence: one entry per connected tab, auto-removed on disconnect ----
  useEffect(() => {
    if (!active || !sessionId || !userName || spectator) return undefined;
    const presenceRef = push(ref(database, `${base}/presence`));
    set(presenceRef, { name: userName, key: identityKey, joinedAt: Date.now() }).catch(() => {});
    const disconnect = onDisconnect(presenceRef);
    disconnect.remove().catch(() => {});
    return () => {
      disconnect.cancel().catch(() => {});
      remove(presenceRef).catch(() => {});
    };
  }, [active, sessionId, base, userName, identityKey, spectator]);

  // ---- Mutations (all granular multi-path writes) ----
  const write = useCallback(
    (paths) => {
      if (spectator) return Promise.resolve();
      return update(ref(database), { ...paths, [`${base}/meta/lastUpdated`]: Date.now() })
        .catch((error) => {
          console.error('Write failed:', error);
          toast("Couldn't save that change, check your connection.");
        });
    },
    [base, spectator]
  );

  const addRow = useCallback(
    (isCritical = false) => write({ [`${base}/rows/${genKey()}`]: { isCritical, createdAt: Date.now() } }),
    [base, write]
  );

  const addDiffRow = useCallback(
    () => write({ [`${base}/diffRows/${genKey()}`]: { createdAt: Date.now() } }),
    [base, write]
  );

  const deleteRow = useCallback(
    (rowId, isDiff = false) => write({
      [`${base}/${isDiff ? 'diffRows' : 'rows'}/${rowId}`]: null,
      [`${base}/items/${rowId}`]: null,
      [`${base}/votes/${rowId}`]: null,
      [`${base}/probabilities/${rowId}`]: null,
    }),
    [base, write]
  );

  // During the individual phase a student's entries go to their own private
  // subtree instead of the shared items node, same rowId/column, so the merge
  // can put them back exactly where they belong.
  const privateWrites = () =>
    getSettings(latest.current.data).phase === 'individual'
    && !latest.current.derived.isFacilitator
    && !spectator;

  const addItem = useCallback(
    (rowId, column, text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      if (privateWrites()) {
        write({
          [`${base}/individual/${identityKey}/${rowId}/${column}/${genKey()}`]: {
            text: trimmed, name: userName, createdAt: Date.now(),
          },
        });
        return;
      }
      write({
        [`${base}/items/${rowId}/${column}/${genKey()}`]: {
          text: trimmed, addedBy: userName, addedByKey: identityKey, createdAt: Date.now(),
        },
      });
    },
    // privateWrites reads through `latest`, so it needs no dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, userName, identityKey, write, spectator]
  );

  const editItem = useCallback(
    (rowId, column, itemId, text, isPrivate = false) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      if (isPrivate) {
        write({ [`${base}/individual/${identityKey}/${rowId}/${column}/${itemId}/text`]: trimmed });
        return;
      }
      write({
        [`${base}/items/${rowId}/${column}/${itemId}/text`]: trimmed,
        [`${base}/items/${rowId}/${column}/${itemId}/editedAt`]: Date.now(),
      });
    },
    [base, identityKey, write]
  );

  // Resolves to a restore() that puts the item (and its votes/probabilities)
  // back exactly as they were, for the removal toast's Undo. The snapshot is
  // read before the write so a concurrent vote can't be lost in between.
  const deleteItem = useCallback(
    async (rowId, column, itemId, isPrivate = false) => {
      if (isPrivate) {
        const path = `${base}/individual/${identityKey}/${rowId}/${column}/${itemId}`;
        const snap = await get(ref(database, path));
        const saved = snap.val();
        await write({ [path]: null });
        return saved ? () => write({ [path]: saved }) : null;
      }
      const itemPath = `${base}/items/${rowId}/${column}/${itemId}`;
      const votesPath = `${base}/votes/${rowId}/${column}/${itemId}`;
      const probPath = `${base}/probabilities/${rowId}/${itemId}`;
      // Probability estimates only exist for diagnoses; including the path
      // for other columns would trip the rules and void the atomic update.
      const isDx = column === 'diagnoses';
      const [item, votes, probs] = await Promise.all([
        get(ref(database, itemPath)).then((s) => s.val()),
        get(ref(database, votesPath)).then((s) => s.val()),
        isDx ? get(ref(database, probPath)).then((s) => s.val()) : null,
      ]);
      await write({
        [itemPath]: null,
        [votesPath]: null,
        ...(isDx ? { [probPath]: null } : {}),
      });
      if (!item) return null;
      return () => write({
        [itemPath]: item,
        ...(votes ? { [votesPath]: votes } : {}),
        ...(probs ? { [probPath]: probs } : {}),
      });
    },
    [base, identityKey, write]
  );

  // Each user transacts only on their own vote leaf, conflict-free by design.
  // Vote-mode rules (single ±1, budget cap) are enforced here.
  const vote = useCallback(
    (rowId, column, itemId, delta) => {
      if (!identityKey || spectator) return;
      const { data: d, derived: dv } = latest.current;
      const settings = getSettings(d);
      const current = voteVal(d?.votes?.[rowId]?.[column]?.[itemId]?.[identityKey]);

      if (settings.voteMode === 'single') {
        const next = Math.max(-1, Math.min(1, current + delta));
        if (next === current) {
          toast('Single-vote mode: one up or down vote per item.', 'info');
          return;
        }
      }
      if (settings.voteMode === 'budget') {
        const next = current + delta;
        const wouldSpend = dv.spentVotes - Math.abs(current) + Math.abs(next);
        if (Math.abs(next) > Math.abs(current) && wouldSpend > settings.voteBudget) {
          toast(`No votes left, you've used ${dv.spentVotes}/${settings.voteBudget}.`, 'info');
          return;
        }
      }

      const leaf = ref(database, `${base}/votes/${rowId}/${column}/${itemId}/${identityKey}`);
      runTransaction(leaf, (cur) => {
        const curVal = voteVal(cur);
        let next = curVal + delta;
        if (settings.voteMode === 'single') next = Math.max(-1, Math.min(1, next));
        if (next === curVal) return undefined; // abort no-op
        return { name: userName, value: next };
      }).catch((error) => {
        console.error('Vote failed:', error);
        toast("Vote didn't register, check your connection.");
      });
    },
    [base, identityKey, userName, spectator]
  );

  // Probability estimates: own leaf only, 0–100, null clears.
  const setProbability = useCallback(
    (rowId, itemId, value) => {
      if (!identityKey || spectator) return;
      const leaf = ref(database, `${base}/probabilities/${rowId}/${itemId}/${identityKey}`);
      const op = value === null
        ? remove(leaf)
        : set(leaf, { name: userName, value: Math.max(0, Math.min(100, Math.round(value))) });
      op.catch((error) => {
        console.error('Probability write failed:', error);
        toast("Estimate didn't save, check your connection.");
      });
    },
    [base, identityKey, userName, spectator]
  );

  // ---- Facilitator actions (client-gated here, rules-gated server-side) ----
  const setSettings = useCallback(
    (patch) => {
      if (!latest.current.derived.isFacilitator) return;
      write(Object.fromEntries(Object.entries(patch).map(([k, v]) => [`${base}/settings/${k}`, v])));
    },
    [base, write]
  );

  const takeSnapshot = useCallback(
    (label) => {
      const { data: d, derived: dv } = latest.current;
      if (!dv.isFacilitator) return;
      const ranking = computeRanking(dv.cdmRows, getSettings(d).probabilityMode);
      if (!ranking.length) return;
      write({
        [`${base}/snapshots/${genKey()}`]: { label: label || 'Snapshot', createdAt: Date.now(), ranking },
      });
    },
    [base, write]
  );

  const addStage = useCallback(
    (title, content) => {
      const { derived: dv, data: d } = latest.current;
      if (!dv.isFacilitator || !title.trim() || !content.trim()) return;
      const order = getStages(d).length;
      write({
        [`${base}/case/stages/${genKey()}`]: {
          title: title.trim(), content: content.trim(), order, createdAt: Date.now(),
        },
      });
    },
    [base, write]
  );

  // Reveal auto-snapshots the pre-reveal ranking, so the timeline captures
  // how the group's thinking stood *before* each new piece of information.
  const revealStage = useCallback(
    (stage) => {
      if (!latest.current.derived.isFacilitator) return;
      takeSnapshot(`Before: ${stage.title}`);
      write({ [`${base}/case/stages/${stage.id}/revealedAt`]: Date.now() });
    },
    [base, write, takeSnapshot]
  );

  // ---- Individual phase (think-alone-then-share) ----
  //
  // Students write to individual/$key/$rowId/$column/$entryId, the same
  // rowId/column coordinates as the shared board, so this merge can collapse
  // duplicates and drop everything back exactly where it was typed.
  //
  // At the same time it saves each student a PERSONAL gap record: what the
  // class listed that they didn't, plus the case findings they never elicited.
  // That record survives the merge (under their private node) and is what the
  // debrief panel shows them at the end.
  const MAX_GAP_ITEMS = 40;

  const mergeIndividual = useCallback(() => {
    const { data: d, derived: dv, individualData: priv } = latest.current;
    if (!dv.isFacilitator) return Promise.resolve(0);
    const cf = dv.caseFlow;
    const diffRowsNode = d?.diffRows || {};
    const BOARD_SCOPE = {
      [HISTORY_ROW_ID]: 'history', [PHYSICAL_ROW_ID]: 'physical', [LABS_ROW_ID]: 'labs',
    };
    const scopeOf = (rowId) => BOARD_SCOPE[rowId] || (diffRowsNode[rowId] ? 'differential' : 'cdm');
    // CDM diagnoses collapse across every row into one fresh ranking (the row
    // IS the rank, so they can't merge in place). Everything else stays put.
    const bucketOf = (rowId, column, scope) =>
      (scope === 'cdm' && column === 'diagnoses' ? '__cdmdx__' : `${rowId} ${column}`);

    // ---- 1. Collect every private entry ----
    const buckets = new Map();  // bucket -> { rowId, column, scope, entries, groups }
    const users = new Map();    // userKey -> { name, texts: Map<scope, string[]> }
    let totalEntries = 0;

    Object.entries(priv || {}).forEach(([userKey, node]) => {
      Object.entries(node || {}).forEach(([rowId, columns]) => {
        if (rowId === '_gaps') return; // a saved gap record is not an entry
        Object.entries(columns || {}).forEach(([column, entries]) => {
          const scope = scopeOf(rowId);
          const bucket = bucketOf(rowId, column, scope);
          Object.values(entries || {}).forEach((e) => {
            if (!normalizeDx(e?.text)) return;
            // A student exists here only once they have written something,
            // so a leftover _gaps node never counts as "missed everything".
            const user = users.get(userKey) || { name: '', texts: new Map(), byColumn: new Map() };
            users.set(userKey, user);
            const b = buckets.get(bucket) || { rowId, column, scope, entries: [], groups: [] };
            buckets.set(bucket, b);
            const myTexts = user.texts.get(scope) || [];
            user.texts.set(scope, myTexts);
            const colKey = `${scope} ${column}`;
            const myColumn = user.byColumn.get(colKey) || [];
            user.byColumn.set(colKey, myColumn);
            totalEntries += 1;
            if (!user.name && e.name) user.name = e.name;
            myTexts.push(e.text);
            myColumn.push(e.text);
            b.entries.push({ text: e.text.trim(), userKey });
          });
        });
      });
    });

    if (!totalEntries) {
      toast('No individual entries to merge yet.', 'info');
      return Promise.resolve(0);
    }

    // Collapse each bucket: the same disease in different words ("ATN",
    // "acute tubular necrosis", a typo) becomes one card, named the way the
    // step's answer key names it, counting every student who listed it (see
    // utils/mergeMatch.js). Evidence merges on wording alone.
    const isEvidence = (column) => column === 'supporting' || column === 'opposing';
    const asExperts = (list, key) => (list || []).map((e) => (typeof e === 'string' ? { text: e } : { text: e[key] || e.text, aliases: e.aliases }));
    const mergeOptions = (scope, column) => {
      if (isEvidence(column)) return { kind: 'evidence', canonical: false };
      const cdm = [...asExperts(cf.cdmAnswer, 'text'), ...(cf.diagnosis ? [cf.diagnosis] : [])];
      const diffDiseases = (cf.diffAnswer || []).flatMap((c) => asExperts(c.diseases, 'text'));
      if (scope === 'cdm') return { kind: 'name', canonical: true, resolve: makeResolver({ primary: cdm, secondary: diffDiseases }) };
      if (scope === 'differential' && column === 'diseases') {
        return { kind: 'name', canonical: true, resolve: makeResolver({ primary: diffDiseases, secondary: cdm }) };
      }
      if (scope === 'differential') {
        return { kind: 'name', canonical: true, resolve: makeResolver({ primary: asExperts(cf.diffAnswer, 'etiologicalClass') }) };
      }
      // Board steps merge on wording alone: a catch-all ("vitals") must not
      // swallow a specific entry, and nothing is renamed before the reveal.
      return { kind: 'evidence', canonical: false };
    };
    buckets.forEach((b) => {
      b.options = mergeOptions(b.scope, b.column);
      b.groups = clusterEntries(b.entries, b.options).map((g) => ({ ...g, options: b.options, column: b.column }));
    });

    // ---- 2. Publish the merged, deduplicated entries ----
    const now = Date.now();
    const paths = { [`${base}/settings/phase`]: 'group' };
    const mkItem = (g, at) => ({
      text: g.text, addedBy: 'Class', addedByKey: identityKey, createdAt: at,
      ...(g.count > 1 ? { listedBy: g.count } : {}),
    });
    // What each scope ended up with, for the per-student gap lists.
    const mergedByScope = new Map();
    const noteMerged = (scope, g) => {
      const list = mergedByScope.get(scope) || [];
      mergedByScope.set(scope, list);
      list.push(g);
    };
    let published = 0;

    // Already on the shared board, in any wording, means don't publish again:
    // the existing card takes the listers' credit instead. `shared` items are
    // { path, text, addedBy, addedByKey, listedBy }.
    const alreadyShared = (g, shared, options) =>
      shared.find((it) => g.members.some((m) => sameEntry(it.text, m.text, options)) || sameEntry(it.text, g.text, options));
    let credited = 0;
    const creditExisting = (it, g) => {
      credited += 1;
      // The card's own author counts as a lister unless it was the class's
      // or the case's card, or they are among the listers already.
      const own = it.addedBy === 'Class' || it.addedBy === 'Case' || it.addedByKey === identityKey || g.users.has(it.addedByKey) ? 0 : 1;
      const listedBy = Math.max(Number(it.listedBy) || 0, g.count + own);
      if (listedBy > 1) paths[`${it.path}/listedBy`] = listedBy;
    };

    buckets.forEach((b, key) => {
      const merged = [...b.groups].sort((a, z) => z.count - a.count || a.text.localeCompare(z.text));
      merged.forEach((g) => noteMerged(b.scope, g));
      if (key === '__cdmdx__') return; // handled below, after the row rebuild
      const shared = Object.entries(d?.items?.[b.rowId]?.[b.column] || {})
        .map(([id, it]) => ({ ...it, text: it?.text || '', path: `${base}/items/${b.rowId}/${b.column}/${id}` }));
      merged.forEach((g, i) => {
        const existing = alreadyShared(g, shared, b.options);
        if (existing) { creditExisting(existing, g); return; }
        published += 1;
        paths[`${base}/items/${b.rowId}/${b.column}/${genKey()}`] = mkItem(g, now + i);
      });
    });

    const cdmBucket = buckets.get('__cdmdx__');
    if (cdmBucket) {
      const merged = [...cdmBucket.groups].sort((a, z) => z.count - a.count || a.text.localeCompare(z.text));
      const shared = dv.cdmRows.flatMap((r) => r.diagnoses.map((x) => ({ ...x, path: `${base}/items/${r.id}/diagnoses/${x.id}` })));
      // Clear empty regular rows so the merged ranking starts at rank 1,
      // except a row the merge is publishing evidence into: one update cannot
      // both null a row and write beneath it.
      const touched = new Set([...buckets.values()].filter((b) => b.scope === 'cdm').map((b) => b.rowId));
      dv.cdmRows
        .filter((r) => !r.isCritical && !r.diagnoses.length && !r.supporting.length && !r.opposing.length && !touched.has(r.id))
        .forEach((r) => {
          paths[`${base}/rows/${r.id}`] = null;
          paths[`${base}/items/${r.id}`] = null;
          paths[`${base}/votes/${r.id}`] = null;
        });
      merged.forEach((g, i) => {
        const existing = alreadyShared(g, shared, cdmBucket.options);
        if (existing) { creditExisting(existing, g); return; }
        published += 1;
        const rowKey = genKey();
        paths[`${base}/rows/${rowKey}`] = { isCritical: false, createdAt: now + i };
        paths[`${base}/items/${rowKey}/diagnoses/${genKey()}`] = mkItem(g, now + i);
      });
    }

    // ---- 3. Save each student their own gap list, then clear their entries ----
    users.forEach((user, userKey) => {
      const scopes = new Set([...mergedByScope.keys(), ...user.texts.keys()]);
      scopes.forEach((scope) => {
        const myTexts = user.texts.get(scope) || [];
        // A peer entry is a gap only if nothing this student wrote on the
        // step, in any column or row, says the same thing; and the same
        // entry filed under two rows is one gap, not two.
        const peers = [];
        (mergedByScope.get(scope) || []).forEach((g) => {
          if (g.users.has(userKey)) return;
          const mine = user.byColumn.get(`${scope} ${g.column}`) || [];
          if (mine.some((t) => g.members.some((m) => sameEntry(t, m.text, g.options)) || sameEntry(t, g.text, g.options))) return;
          const twin = peers.find((p) => sameEntry(p.text, g.text, g.options));
          if (twin) { twin.listedBy = Math.max(twin.listedBy, g.count); return; }
          peers.push({ text: g.text, listedBy: g.count });
        });
        peers.splice(MAX_GAP_ITEMS);
        // Case findings this student personally never elicited (board steps).
        const findings = (cf.active ? (cf.findings[scope] || []) : [])
          .filter((f) => !myTexts.some((t) => fuzzyMatch(t, f, { tokens: true })))
          .slice(0, MAX_GAP_ITEMS)
          .map((f) => ({ text: f.text, detail: f.detail || '' }));
        if (!peers.length && !findings.length) return;
        paths[`${base}/individual/${userKey}/_gaps/${scope}`] = {
          label: GAP_LABELS[scope] || scope,
          mergedAt: now,
          mineCount: myTexts.length,
          peers,
          findings,
        };
      });
      // Their entries are now shared (and recorded above), clear the originals
      // so a later individual phase starts clean.
      Object.keys(priv?.[userKey] || {}).forEach((rowId) => {
        if (rowId !== '_gaps') paths[`${base}/individual/${userKey}/${rowId}`] = null;
      });
    });

    // ---- 4. The class-level view of the same gaps, for the facilitator ----
    // Everyone present counts as the room, plus anyone who contributed.
    const present = presentStudentKeys(d?.presence, d?.meta?.facilitatorKey);
    users.forEach((_, key) => present.add(key));
    const scopes = new Set(mergedByScope.keys());
    users.forEach((u) => u.texts.forEach((_, scope) => scopes.add(scope)));
    if (cf.active) {
      scopes.forEach((scope) => {
        const texts = new Map();
        users.forEach((u, key) => { if (u.texts.has(scope)) texts.set(key, u.texts.get(scope)); });
        paths[`${base}/caseFlow/classGaps/${scope}`] = computeClassGaps(cf, scope, texts, present.size);
        // The CDM grid is carried forward from the differential, so the expert
        // CDM answer is scored against the same private differential entries.
        if (scope === 'differential') {
          paths[`${base}/caseFlow/classGaps/cdm`] = computeClassGaps(cf, 'cdm', texts, present.size);
        }
      });
    }

    return write(paths).then(() => published + credited);
  }, [base, identityKey, write]);

  // Batch-add missing must-not-miss diagnoses as Critical Dx rows.
  const addCriticalDiagnoses = useCallback(
    (names) => {
      const now = Date.now();
      const paths = {};
      names.forEach((name, i) => {
        const rowKey = genKey();
        paths[`${base}/rows/${rowKey}`] = { isCritical: true, createdAt: now + i };
        paths[`${base}/items/${rowKey}/diagnoses/${genKey()}`] = {
          text: name, addedBy: userName, addedByKey: identityKey, createdAt: now + i,
        };
      });
      if (Object.keys(paths).length) write(paths);
    },
    [base, userName, identityKey, write]
  );

  // ---- Guided case flow (facilitator-gated client-side; rules-gated server-side) ----
  const facGuard = () => latest.current.derived.isFacilitator;

  /**
   * Loads a preset (or blank custom) case and starts the flow at History.
   *
   * `streamIds` runs the case's diagnostic variants (see utils/caseStreams):
   * the case is flattened against `streamIds[streamIndex]`, and the queue is
   * stored so the facilitator can walk several variants in one lecture.
   * `wipe: true` also clears the previous variant's student work.
   */
  const loadCase = useCallback(
    (rawCase, { streamIds = [], streamIndex = 0, wipe = false } = {}) => {
      if (!facGuard()) return undefined;
      const queue = (streamIds || []).filter(Boolean);
      const streamId = queue[streamIndex] || null;
      const caseDef = resolveCase(rawCase, streamId);
      const stream = streamInfo(rawCase, streamId, queue, streamIndex);
      const keyed = (arr = []) => {
        const out = {};
        arr.forEach((entry, i) => { out[`f${String(i).padStart(3, '0')}`] = entry; });
        return out;
      };
      const now = Date.now();
      const paths = {
        [`${base}/caseFlow`]: {
          active: true,
          caseId: caseDef.id || 'custom',
          // The definition this run came from, so the next variant can be
          // resolved from the library without re-picking the case.
          sourceCaseId: rawCase.id || 'custom',
          ...(stream ? { stream } : {}),
          title: caseDef.title || 'Custom case',
          presentingSymptom: caseDef.presentingSymptom || '',
          demographics: caseDef.demographics || '',
          step: 'history',
          createdAt: now,
          findings: {
            history: keyed(caseDef.history),
            physical: keyed(caseDef.physical),
            labs: keyed(caseDef.labs),
          },
          diagnosis: caseDef.diagnosis || null,
          diffAnswer: keyed(caseDef.diffAnswer),
          cdmAnswer: keyed(caseDef.cdmAnswer),
          problemRepresentation: caseDef.problemRepresentation || null,
        },
      };
      if (wipe) {
        Object.assign(paths, {
          [`${base}/items`]: null,
          [`${base}/votes`]: null,
          [`${base}/probabilities`]: null,
          [`${base}/snapshots`]: null,
          [`${base}/individual`]: null,
          [`${base}/rows`]: seedRows(),
          [`${base}/diffRows`]: seedDiffRows(),
        });
      }
      return write(paths);
    },
    [base, write]
  );

  // Starts the next variant in the queue on a clean board. `rawCase` is the
  // source definition (looked up by caseFlow.sourceCaseId by the caller).
  const nextStream = useCallback(
    (rawCase) => {
      const cf = latest.current.derived.caseFlow;
      const stream = cf.stream;
      if (!facGuard() || !stream?.nextId) return undefined;
      return loadCase(rawCase, {
        streamIds: stream.queue,
        streamIndex: stream.index + 1,
        wipe: true,
      });
    },
    [loadCase]
  );

  const endCase = useCallback(() => {
    if (!facGuard()) return;
    write({ [`${base}/caseFlow`]: null });
  }, [base, write]);

  // Restart the same case from scratch for a fresh group: wipes every student
  // contribution and all reveal state, keeps the case's answer key intact.
  const resetCase = useCallback(() => {
    const { derived: dv } = latest.current;
    if (!facGuard() || !dv.caseFlow.active) return;
    const now = Date.now();
    const paths = {
      [`${base}/caseFlow/step`]: 'history',
      [`${base}/caseFlow/completed`]: null,
      [`${base}/caseFlow/labsRevealedAt`]: null,
      [`${base}/caseFlow/diagnosisRevealedAt`]: null,
      [`${base}/caseFlow/overrides`]: null,
      [`${base}/caseFlow/classGaps`]: null,
      // Student work: brainstorm boards, the grid, votes, and the timeline.
      [`${base}/items`]: null,
      [`${base}/votes`]: null,
      [`${base}/probabilities`]: null,
      [`${base}/snapshots`]: null,
      [`${base}/individual`]: null,
      [`${base}/rows`]: seedRows(),
      [`${base}/diffRows`]: seedDiffRows(),
      [`${base}/meta/lastUpdated`]: now,
    };
    return update(ref(database), paths).catch((error) => {
      console.error('Reset failed:', error);
      toast('Could not reset the case, check your connection.');
    });
  }, [base]);

  // Marks the current step revealed, board entries light up / grey out.
  // On the Differential step there is no board: instead the expert differential
  // is autofilled, merging into the class's own etiological-class rows where
  // they already exist so nothing is duplicated.
  const revealMatches = useCallback(
    async (tab) => {
      if (!facGuard()) return;
      // Reveal ends the individual phase: everyone's private entries are
      // merged (and each student's personal gaps saved) first, so the reveal
      // then answers the merged board. Awaited so the differential autofill
      // below sees the merged rows and can't duplicate them.
      const wasIndividual = getSettings(latest.current.data).phase === 'individual';
      if (wasIndividual) {
        await mergeIndividual();
      }
      const { derived: dv, data: d } = latest.current;
      const now = Date.now();
      const paths = { [`${base}/caseFlow/completed/${tab}`]: now };

      // Class gaps for a group-mode step. A merge writes them itself, and once
      // written they stand: the merged board is attributed to "Class", so
      // per-student attribution is gone from the shared items by then.
      if (dv.caseFlow.active && !wasIndividual && !dv.caseFlow.classGaps?.[tab]) {
        const facKey = d?.meta?.facilitatorKey;
        const BOARD_ROW = { [HISTORY_ROW_ID]: 'history', [PHYSICAL_ROW_ID]: 'physical', [LABS_ROW_ID]: 'labs' };
        const rowKey = Object.keys(BOARD_ROW).find((k) => BOARD_ROW[k] === tab);
        const nodes = tab === 'differential'
          ? Object.keys(d?.diffRows || {}).map((rowId) => d?.items?.[rowId]?.diseases)
          : Object.values(d?.items?.[rowKey] || {});
        const texts = textsFromSharedItems(nodes, { facilitatorKey: facKey });
        const present = presentStudentKeys(d?.presence, facKey);
        texts.forEach((_, key) => present.add(key));
        paths[`${base}/caseFlow/classGaps/${tab}`] = computeClassGaps(dv.caseFlow, tab, texts, present.size);
        if (tab === 'differential') {
          paths[`${base}/caseFlow/classGaps/cdm`] = computeClassGaps(dv.caseFlow, 'cdm', texts, present.size);
        }
      }

      if (tab === 'differential') {
        const expert = dv.caseFlow.diffAnswer || [];
        const emptyRows = dv.diffRows
          .filter((r) => !r.etiologicalClass.length && !r.diseases.length && !r.supporting.length && !r.opposing.length)
          .map((r) => r.id);
        let emptyUsed = 0;
        const mkItem = (text, at) => ({ text, addedBy: 'Case', addedByKey: identityKey, createdAt: at });
        // As with the CDM reveal: the expert's sequence becomes the row order,
        // whether the class already owned that etiological class or the reveal
        // is adding it. Anything the expert didn't name follows.
        const claimed = new Set();
        let seq = 0;
        const nextOrder = () => {
          const order = now + seq * 10;
          seq += 1;
          return order;
        };

        expert.forEach((entry, i) => {
          // Reuse the class's row if they already named this etiological class.
          const existing = dv.diffRows.find((r) =>
            r.etiologicalClass.some((e) => fuzzyMatch(e.text, entry)));
          const order = nextOrder();
          const reused = !existing && emptyRows[emptyUsed];
          const rowId = existing ? existing.id : (reused || genKey());
          if (existing) {
            if (!claimed.has(rowId)) paths[`${base}/diffRows/${rowId}/createdAt`] = order;
          } else if (reused) {
            emptyUsed += 1;
            paths[`${base}/diffRows/${rowId}/createdAt`] = order;
          } else {
            paths[`${base}/diffRows/${rowId}`] = { createdAt: order };
          }
          claimed.add(rowId);
          if (!existing) {
            paths[`${base}/items/${rowId}/etiologicalClass/${genKey()}`] = mkItem(entry.etiologicalClass, now + i);
          }
          // Skip what the class already has, in any wording (the merge names
          // its cards after this very answer key, so usually exactly).
          const has = (col, text, kind) => !!existing
            && existing[col].some((it) => sameEntry(it.text, text, { kind }));
          (entry.diseases || []).forEach((text, j) => {
            if (has('diseases', text, 'name')) return;
            paths[`${base}/items/${rowId}/diseases/${genKey()}`] = mkItem(text, now + i * 100 + j);
          });
          (entry.supporting || []).forEach((text, j) => {
            if (has('supporting', text, 'name')) return;
            paths[`${base}/items/${rowId}/supporting/${genKey()}`] = mkItem(text, now + i * 100 + j + 20);
          });
          (entry.opposing || []).forEach((text, j) => {
            if (has('opposing', text, 'name')) return;
            paths[`${base}/items/${rowId}/opposing/${genKey()}`] = mkItem(text, now + i * 100 + j + 40);
          });
        });

        dv.diffRows
          .filter((r) => !claimed.has(r.id))
          .forEach((r) => { paths[`${base}/diffRows/${r.id}/createdAt`] = nextOrder(); });
      }

      write(paths);
    },
    [base, identityKey, write, mergeIndividual]
  );

  // Moves everyone to the next step. Entering CDM carries the class's
  // differential diseases forward into the grid (deduped, filling empty rows
  // first, adding as many rows as needed).
  const advanceCase = useCallback(async () => {
    if (!facGuard()) return;
    // Leaving a step while the individual phase is still live would strand
    // everyone's private entries, publish them first, as the reveal does.
    if (getSettings(latest.current.data).phase === 'individual') {
      await mergeIndividual();
    }
    const { derived: dv } = latest.current;
    const cf = dv.caseFlow;
    if (!cf.active) return;
    const next = STEP_ORDER[stepIndex(cf.step) + 1];
    if (!next) return;
    const now = Date.now();
    const paths = { [`${base}/caseFlow/step`]: next };
    if (!cf.completed[cf.step]) paths[`${base}/caseFlow/completed/${cf.step}`] = now;
    if (next === 'cdm') {
      const existing = new Set(
        dv.cdmRows.flatMap((r) => r.diagnoses.map((d) => normalizeDx(d.text)))
      );
      const carried = [];
      dv.diffRows.forEach((r) => r.diseases.forEach((d) => {
        // Only the class's own diseases move forward. Expert entries added by
        // the differential reveal stay there, so the CDM grid remains the
        // class's working ranking and the final reveal still has work to do.
        if (d.addedBy === 'Case') return;
        const norm = normalizeDx(d.text);
        if (!norm || existing.has(norm)) return;
        existing.add(norm);
        carried.push(d.text);
      }));
      const emptyRows = dv.cdmRows
        .filter((r) => !r.isCritical && !r.diagnoses.length && !r.supporting.length && !r.opposing.length)
        .map((r) => r.id);
      // `carried` already arrives in the class's own order (diffRows are sorted
      // by vote, diseases by createdAt), so keep it: sequence every row, reused
      // or new, instead of letting seeded timestamps shuffle it.
      carried.forEach((text, i) => {
        const order = now + i * 10;
        const rowId = emptyRows[i] || genKey();
        if (emptyRows[i]) paths[`${base}/rows/${rowId}/createdAt`] = order;
        else paths[`${base}/rows/${rowId}`] = { isCritical: false, createdAt: order };
        paths[`${base}/items/${rowId}/diagnoses/${genKey()}`] = {
          text, addedBy: 'Class', addedByKey: identityKey, createdAt: order,
        };
      });
    }
    write(paths);
  }, [base, identityKey, write, mergeIndividual]);

  // Reveals the answer AND autofills the expert differential into the CDM
  // grid: any cdmAnswer entry the class didn't list gets its own row (critical
  // where flagged) with the model supporting/opposing evidence, addedBy "Case".
  const revealDiagnosis = useCallback(() => {
    const { derived: dv } = latest.current;
    const cf = dv.caseFlow;
    if (!facGuard()) return;
    takeSnapshot('Before: Final diagnosis');
    const now = Date.now();
    const paths = {
      [`${base}/caseFlow/diagnosisRevealedAt`]: now,
      [`${base}/caseFlow/completed/cdm`]: now,
    };
    const emptyRows = dv.cdmRows
      .filter((r) => !r.isCritical && !r.diagnoses.length && !r.supporting.length && !r.opposing.length)
      .map((r) => r.id);

    // The reveal re-lays the grid in the EXPERT's order: rows tie on score
    // (nothing is voted at this point), so a row's createdAt is its position.
    // Every cdmAnswer entry takes the next slot whether the class already had
    // it or the reveal is adding it, and whatever the expert never mentioned
    // follows, keeping its relative order. Votes still win where they exist —
    // the class's own ranking is preserved above, and in the timeline snapshot
    // taken a few lines up.
    const claimed = new Set();
    let filled = 0;
    let seq = 0;
    const nextOrder = () => {
      const order = now + seq * 10;
      seq += 1;
      return order;
    };

    (cf.cdmAnswer || []).forEach((entry) => {
      const order = nextOrder();
      const mine = dv.cdmRows.find((r) => r.diagnoses.some((d) => fuzzyMatch(d.text, entry)));
      if (mine) {
        // The class listed it: move their row into the expert's slot, add nothing.
        if (!claimed.has(mine.id)) {
          claimed.add(mine.id);
          paths[`${base}/rows/${mine.id}/createdAt`] = order;
        }
        return;
      }
      const reused = !entry.isCritical && emptyRows[filled];
      const rowId = reused || genKey();
      if (reused) {
        filled += 1;
        claimed.add(rowId);
        paths[`${base}/rows/${rowId}/createdAt`] = order;
      } else {
        paths[`${base}/rows/${rowId}`] = { isCritical: !!entry.isCritical, createdAt: order };
      }
      const mkItem = (text, j) => ({ text, addedBy: 'Case', addedByKey: identityKey, createdAt: order + j });
      paths[`${base}/items/${rowId}/diagnoses/${genKey()}`] = mkItem(entry.text, 0);
      (entry.supporting || []).forEach((text, j) => {
        paths[`${base}/items/${rowId}/supporting/${genKey()}`] = mkItem(text, j + 1);
      });
      (entry.opposing || []).forEach((text, j) => {
        paths[`${base}/items/${rowId}/opposing/${genKey()}`] = mkItem(text, j + 20);
      });
    });

    // Rows the expert never named (including any leftover blanks) sit after.
    dv.cdmRows
      .filter((r) => !claimed.has(r.id))
      .forEach((r) => { paths[`${base}/rows/${r.id}/createdAt`] = nextOrder(); });

    write(paths);
  }, [base, identityKey, write, takeSnapshot]);

  // Manual match correction: true = force match, false = force miss, null = auto.
  const setMatchOverride = useCallback(
    (itemId, value) => {
      if (!facGuard()) return;
      write({ [`${base}/caseFlow/overrides/${itemId}`]: value });
    },
    [base, write]
  );

  const addFinding = useCallback(
    (tab, { text, detail, category, flag }) => {
      if (!facGuard() || !(text || '').trim()) return;
      write({
        [`${base}/caseFlow/findings/${tab}/${genKey()}`]: {
          text: text.trim(),
          detail: (detail || '').trim(),
          category: category || null,
          ...(flag ? { flag } : {}),
        },
      });
    },
    [base, write]
  );

  const removeFinding = useCallback(
    (tab, findingId) => {
      if (!facGuard()) return;
      write({ [`${base}/caseFlow/findings/${tab}/${findingId}`]: null });
    },
    [base, write]
  );

  // Labs are added/removed through the Labs tab's answer key
  // (addFinding/removeFinding with tab='labs'), which carries the flag picker.

  const setDiagnosis = useCallback(
    (text, explanation) => {
      if (!facGuard() || !(text || '').trim()) return;
      write({ [`${base}/caseFlow/diagnosis`]: { text: text.trim(), explanation: (explanation || '').trim() } });
    },
    [base, write]
  );

  // ---- Archive (facilitator's private library) ----
  const archiveSession = useCallback(
    (title) => {
      const { data: d } = latest.current;
      const payload = {};
      ['meta', 'settings', 'rows', 'diffRows', 'items', 'votes', 'probabilities', 'case', 'snapshots', 'caseFlow']
        .forEach((k) => { if (d[k] != null) payload[k] = d[k]; });
      return set(ref(database, `archives/${identityKey}/${genKey()}`), {
        title: (title || '').trim() || sessionId,
        archivedAt: Date.now(),
        sourceSessionId: sessionId,
        data: payload,
      }).then(() => toast('Session archived to your library.', 'success'))
        .catch((error) => {
          console.error('Archive failed:', error);
          toast('Archiving failed, check your connection.');
        });
    },
    [identityKey, sessionId]
  );

  // ---- Derived extras ----
  const stages = useMemo(() => getStages(data), [data]);
  const snapshots = useMemo(() => getSnapshots(data), [data]);

  // My own saved individual-phase gaps (written by the merge, kept afterwards).
  const myGaps = useMemo(() => getMyGaps(myPrivate), [myPrivate]);

  // Live submission stats for the facilitator's banner: entries live at
  // individual/$uid/$rowId/$column/$entryId, so count the leaves.
  const individualStats = useMemo(() => {
    if (!isFacilitator) return { students: 0, entries: 0 };
    let students = 0;
    let entries = 0;
    Object.values(individualData || {}).forEach((node) => {
      let mine = 0;
      Object.entries(node || {}).forEach(([rowId, columns]) => {
        if (rowId === '_gaps') return;
        Object.values(columns || {}).forEach((col) => { mine += Object.keys(col || {}).length; });
      });
      if (mine > 0) students += 1;
      entries += mine;
    });
    return { students, entries };
  }, [individualData, isFacilitator]);

  return {
    loading,
    ...derived, // settings, meta, isFacilitator, revealed, cdmRows, diffRows, onlineUsers, spentVotes
    stages,
    snapshots,
    myGaps,
    individualStats,
    addRow, addDiffRow, deleteRow,
    addItem, editItem, deleteItem,
    vote, setProbability,
    setSettings, takeSnapshot, addStage, revealStage,
    mergeIndividual,
    addCriticalDiagnoses,
    loadCase, nextStream, endCase, resetCase, revealMatches, advanceCase, revealDiagnosis,
    setMatchOverride, addFinding, removeFinding, setDiagnosis,
    archiveSession,
  };
}
